import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Inbox, Mail, Phone, Trash2, CheckCircle2, Circle, Loader2 } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Submissions = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/submissions`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(r.data);
    } catch { setItems([]); }
  };

  useEffect(() => { if (token) load(); }, [token]);

  const markRead = async (id) => {
    await axios.post(`${API}/admin/submissions/${id}/read`, {}, { headers: { Authorization: `Bearer ${token}` } });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this submission?')) return;
    await axios.delete(`${API}/admin/submissions/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    setSelected(null); load();
    toast({ title: 'Deleted', description: 'Submission removed.' });
  };

  return (
    <AdminLayout>
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow">Inbox</span></div>
        <h1 className="font-serif-display text-4xl font-bold text-black">Contact Submissions</h1>
      </div>

      {items === null ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#c8102e]" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white p-12 border border-neutral-200 text-center">
          <Inbox className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
          <p className="text-neutral-500">No submissions yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-[380px_1fr] gap-4">
          <div className="space-y-2">
            {items.map((it) => (
              <button key={it.id} onClick={() => { setSelected(it); if (!it.read) markRead(it.id); }} className={`w-full text-left p-4 border transition-colors ${selected?.id === it.id ? 'border-black bg-white' : 'border-neutral-200 bg-white hover:border-neutral-400'}`}>
                <div className="flex items-center gap-2 mb-1">
                  {it.read ? <CheckCircle2 className="w-3.5 h-3.5 text-neutral-300" /> : <Circle className="w-3.5 h-3.5 text-[#c8102e] fill-[#c8102e]" />}
                  <span className="font-serif-display font-bold text-black text-sm truncate">{it.name}</span>
                </div>
                <div className="text-xs text-neutral-500 truncate">{it.subject || it.message.slice(0, 60)}</div>
                <div className="text-[10px] text-neutral-400 mt-1">{new Date(it.created_at).toLocaleString()}</div>
              </button>
            ))}
          </div>
          <div className="bg-white border border-neutral-200 p-8 min-h-[400px]">
            {selected ? (
              <>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="text-[10px] tracking-[0.25em] uppercase text-[#c8102e] font-semibold mb-1">Message</div>
                    <h2 className="font-serif-display text-2xl font-bold text-black">{selected.subject || 'No subject'}</h2>
                  </div>
                  <button onClick={() => remove(selected.id)} className="text-neutral-400 hover:text-[#c8102e]"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="space-y-2 text-sm text-neutral-600 mb-6">
                  <div><span className="font-semibold text-black">{selected.name}</span> <span className="text-neutral-400">• {new Date(selected.created_at).toLocaleString()}</span></div>
                  <a href={`mailto:${selected.email}`} className="flex items-center gap-2 hover:text-[#c8102e]"><Mail className="w-3.5 h-3.5" /> {selected.email}</a>
                  {selected.phone && <a href={`tel:${selected.phone}`} className="flex items-center gap-2 hover:text-[#c8102e]"><Phone className="w-3.5 h-3.5" /> {selected.phone}</a>}
                </div>
                <div className="prose text-neutral-700 whitespace-pre-wrap leading-relaxed border-t border-neutral-100 pt-4">{selected.message}</div>
              </>
            ) : (
              <div className="h-full flex items-center justify-center text-neutral-400 text-sm">Select a submission to read it</div>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Submissions;
